import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 泛知识与生活百科 — 家庭急救三分钟手册
 *
 * 医疗白底 + 红十字 + 五大场景翻牌 + 三步法 + 倒计时心率脉搏
 * 叙事弧：开场倒计时挂钩 → 五个场景循环展示（每个场景：识别 → 急救 → 送医） → 急救包清单 → 黄金原则
 * 尺寸：1920 × 1080 (横屏)
 */

interface FirstAidScene {
  scenario: string;
  scenarioEn: string;
  emoji: string;
  signs: string[];
  steps: { action: string; detail: string; icon: string }[];
  timeWindow: string;
  warning: string;
  themeColor: string;
}

interface FirstAidKitItem {
  name: string;
  icon: string;
  use: string;
}

interface EmergencyFirstAidProps {
  topic: string;
  subtitle: string;
  hookLine: string;
  scenes: FirstAidScene[];
  kit: FirstAidKitItem[];
  goldenPrinciples: string[];
  emergencyHotline: { number: string; label: string }[];
  closingCall: string;
}

const DEFAULT_PROPS: EmergencyFirstAidProps = {
  topic: '关键 3 分钟 · 家庭急救手册',
  subtitle: 'Critical 3 Minutes — Home First Aid',
  hookLine:
    '心脏骤停后，每延迟 1 分钟急救，存活率下降 7-10%。120 救护车到达通常需要 8-12 分钟——抢救生命，靠的是第一目击者。',
  scenes: [
    {
      scenario: '心脏骤停',
      scenarioEn: 'Cardiac Arrest',
      emoji: '💔',
      signs: ['突然倒地无意识', '呼吸停止或濒死叹息', '颈动脉无搏动'],
      steps: [
        { action: '判断意识 + 拨打 120', detail: '轻拍肩膀大声呼叫，立即让旁人拨打 120 并取 AED', icon: '📞' },
        { action: '胸外按压 30 次', detail: '两乳头连线中点，深度 5-6cm，频率 100-120 次/分', icon: '🫀' },
        { action: '人工呼吸 2 次', detail: '抬下颌开放气道，每次吹气 1 秒看到胸廓起伏', icon: '🫁' },
      ],
      timeWindow: '黄金 4 分钟',
      warning: '不要停 — 持续按压直到 AED 到达或患者恢复自主呼吸',
      themeColor: '#dc2626',
    },
    {
      scenario: '气道异物梗阻',
      scenarioEn: 'Choking',
      emoji: '😵',
      signs: ['双手抓喉（万国通用窒息手势）', '无法发声、咳嗽', '面部青紫'],
      steps: [
        { action: '问"你噎到了吗"', detail: '若能咳嗽，鼓励持续咳；若不能，立即施救', icon: '❓' },
        { action: '海姆立克法', detail: '从背后环抱，握拳置于肚脐上方，向内向上快速冲击', icon: '✊' },
        { action: '重复 5 次', detail: '直到异物排出或患者失去意识，失意识则改 CPR', icon: '🔄' },
      ],
      timeWindow: '黄金 2 分钟',
      warning: '婴儿（< 1 岁）禁用海姆立克 — 改用 5 次背部拍击 + 5 次胸部冲击',
      themeColor: '#ea580c',
    },
    {
      scenario: '严重出血',
      scenarioEn: 'Severe Bleeding',
      emoji: '🩸',
      signs: ['鲜红色喷射状血流（动脉）', '伤口渗血不止', '面色苍白、心率加快'],
      steps: [
        { action: '直接加压止血', detail: '干净布料叠厚覆盖伤口，用力按压 5-10 分钟不放松', icon: '🩹' },
        { action: '抬高伤肢', detail: '高于心脏位置，减缓血液回流速度', icon: '☝️' },
        { action: '加压包扎', detail: '弹力绷带加压固定，记录止血时间', icon: '🩻' },
      ],
      timeWindow: '黄金 5 分钟',
      warning: '不要随意用止血带 — 错误使用可能导致组织坏死、截肢',
      themeColor: '#b91c1c',
    },
    {
      scenario: '烫伤烧伤',
      scenarioEn: 'Burns & Scalds',
      emoji: '🔥',
      signs: ['皮肤红肿（一度）', '水泡（二度）', '焦黑无痛感（三度，最严重）'],
      steps: [
        { action: '冷水冲 20 分钟', detail: '15-20°C 流动冷水，降温减少深层损伤', icon: '💧' },
        { action: '脱去衣物', detail: '小心剪开衣服，禁止撕扯粘连皮肤', icon: '✂️' },
        { action: '覆盖送医', detail: '清洁纱布覆盖，禁涂牙膏酱油，二度以上立即就医', icon: '🏥' },
      ],
      timeWindow: '受伤后 30 分钟内',
      warning: '禁止挑破水泡 — 水泡是天然的防菌屏障',
      themeColor: '#f97316',
    },
    {
      scenario: '中风发作',
      scenarioEn: 'Stroke',
      emoji: '🧠',
      signs: ['一侧面部下垂', '一侧手臂无力下垂', '说话不清、口齿含糊'],
      steps: [
        { action: 'FAST 评估', detail: 'F 脸 / A 手 / S 说话 / T 时间 — 任一异常立即送医', icon: '🔍' },
        { action: '记录发病时间', detail: '溶栓需在 4.5 小时内进行，时间就是脑细胞', icon: '⏱️' },
        { action: '侧卧防呛', detail: '让患者侧卧，松开领口，禁喂食物药物', icon: '🛌' },
      ],
      timeWindow: '黄金 4.5 小时',
      warning: '不要让患者"睡一会儿就好" — 90% 的中风可以通过及时溶栓减轻后遗症',
      themeColor: '#7c3aed',
    },
  ],
  kit: [
    { name: '医用纱布 / 创可贴', icon: '🩹', use: '止血、覆盖伤口' },
    { name: '消毒酒精 / 碘伏', icon: '🧴', use: '伤口消毒' },
    { name: '弹力绷带', icon: '🪢', use: '加压固定' },
    { name: '剪刀 + 镊子', icon: '✂️', use: '剪开衣物 / 取异物' },
    { name: '体温计 + 一次性手套', icon: '🌡️', use: '测温 / 防交叉感染' },
    { name: '硝酸甘油 / 阿司匹林', icon: '💊', use: '心绞痛应急' },
    { name: '保鲜膜', icon: '🧊', use: '烧伤覆盖（保鲜膜不粘伤口）' },
    { name: '应急联系卡', icon: '📇', use: '记录血型、过敏史、家属电话' },
  ],
  goldenPrinciples: [
    '保护自己 → 评估环境 → 救助伤者（顺序不能颠倒）',
    '不知道做什么时，做"心肺复苏"几乎不会错',
    '永远先打 120，再开始急救（让专业人员同步出发）',
    '不要随便挪动可能脊柱受伤的患者',
  ],
  emergencyHotline: [
    { number: '120', label: '医疗急救' },
    { number: '119', label: '消防火警' },
    { number: '110', label: '公安报警' },
  ],
  closingCall: '今天就把这条收藏 — 你永远不知道哪一刻，需要救的会是谁。',
};

export const EmergencyFirstAidTemplate: React.FC<
  Partial<EmergencyFirstAidProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleEnd = 60;
  const sceneDuration = 90;
  const totalSceneFrames = sceneDuration * p.scenes.length;
  const sceneEnd = titleEnd + totalSceneFrames;
  const kitStart = sceneEnd + 10;
  const principleStart = kitStart + 90;
  const closingStart = principleStart + 80;
  const totalFrames = closingStart + 80;

  const titleProgress = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const sceneFloat = interpolate(
    frame,
    [titleEnd, sceneEnd],
    [0, p.scenes.length - 0.001],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );
  const currentSceneIdx = Math.floor(sceneFloat);
  const currentScene = p.scenes[currentSceneIdx];

  const heartRate = 1 + Math.sin(frame * 0.18) * 0.04;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #fefefe 0%, #f1f5f9 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: '#0f172a',
        overflow: 'hidden',
      }}
    >
      {/* Subtle medical pattern */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 20% 30%, rgba(220,38,38,0.03), transparent 40%), radial-gradient(circle at 80% 80%, rgba(220,38,38,0.03), transparent 40%)',
        }}
      />
      {/* Scrolling EKG line in background */}
      <svg
        width="1920"
        height="80"
        style={{ position: 'absolute', bottom: 30, left: 0, opacity: 0.15 }}
      >
        {(() => {
          const points: string[] = [];
          for (let x = 0; x < 1920; x += 4) {
            const phase = (x + frame * 4) % 200;
            let y = 40;
            if (phase < 80) y = 40;
            else if (phase < 90) y = 30;
            else if (phase < 95) y = 70;
            else if (phase < 100) y = 8;
            else if (phase < 110) y = 60;
            else if (phase < 120) y = 38;
            else y = 40;
            points.push(`${x},${y}`);
          }
          return <polyline points={points.join(' ')} fill="none" stroke="#dc2626" strokeWidth="2" />;
        })()}
      </svg>

      {/* ===== Header (always visible) ===== */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 60,
          right: 60,
          opacity: titleProgress,
          transform: `translateY(${(1 - titleProgress) * 16}px)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <div
            style={{
              width: 60,
              height: 60,
              background: '#dc2626',
              color: '#fff',
              fontSize: 40,
              fontWeight: 900,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 14,
              transform: `scale(${heartRate})`,
              boxShadow: '0 6px 20px rgba(220,38,38,0.3)',
              flexShrink: 0,
            }}
          >
            +
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                fontSize: 44,
                fontWeight: 900,
                margin: 0,
                color: '#0f172a',
                letterSpacing: '-0.01em',
                lineHeight: 1.1,
              }}
            >
              {p.topic}
            </h1>
            <div style={{ fontSize: 26, color: '#64748b', marginTop: 4 }}>
              {p.subtitle}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
            {p.emergencyHotline.map((h, i) => (
              <div
                key={`hl-${i}`}
                style={{
                  padding: '8px 16px',
                  background: i === 0 ? '#fee2e2' : '#fff',
                  border: '2px solid #dc2626',
                  borderRadius: 12,
                  textAlign: 'center',
                  minWidth: 84,
                }}
              >
                <div style={{ fontSize: 20, color: '#7f1d1d', fontWeight: 700 }}>{h.label}</div>
                <div style={{ fontSize: 30, fontWeight: 900, color: '#dc2626', lineHeight: 1 }}>{h.number}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== Hook (only intro) ===== */}
      {frame < titleEnd + 10 && (
        <div
          style={{
            position: 'absolute',
            top: 180,
            left: 120,
            right: 120,
            bottom: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: interpolate(frame, [10, 40, titleEnd, titleEnd + 10], [0, 1, 1, 0], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            }),
          }}
        >
          <div
            style={{
              padding: '40px 56px',
              background: '#fef2f2',
              borderLeft: '8px solid #dc2626',
              borderRadius: '4px 20px 20px 4px',
              fontSize: 38,
              lineHeight: 1.55,
              color: '#7f1d1d',
              maxWidth: 1500,
              fontWeight: 600,
              boxShadow: '0 12px 40px rgba(220,38,38,0.12)',
            }}
          >
            <span style={{ fontSize: 44, marginRight: 12 }}>⚠</span>
            {p.hookLine}
          </div>
        </div>
      )}

      {/* ===== Scene cards ===== */}
      {frame >= titleEnd - 5 && frame < sceneEnd + 10 && currentScene && (() => {
        const sceneStart = titleEnd + currentSceneIdx * sceneDuration;
        const localFrame = frame - sceneStart;
        const cardEnter = spring({
          frame: localFrame,
          fps,
          from: 0.94,
          to: 1,
          config: { damping: 16, stiffness: 100 },
        });
        const cardOpacity = interpolate(localFrame, [0, 12], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const sceneExit = interpolate(frame, [sceneEnd, sceneEnd + 10], [1, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        return (
          <div
            style={{
              position: 'absolute',
              top: 150,
              left: 60,
              right: 60,
              bottom: 140,
              display: 'flex',
              flexDirection: 'column',
              opacity: cardOpacity * sceneExit,
              transform: `scale(${cardEnter})`,
            }}
          >
            {/* Scene header banner */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 22,
                padding: '20px 28px',
                background: `linear-gradient(135deg, ${currentScene.themeColor}, ${currentScene.themeColor}dd)`,
                borderRadius: '20px 20px 0 0',
                boxShadow: `0 8px 24px ${currentScene.themeColor}55`,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 90,
                  height: 90,
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 56,
                  flexShrink: 0,
                }}
              >
                {currentScene.emoji}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 20,
                    color: 'rgba(255,255,255,0.85)',
                    letterSpacing: '0.18em',
                    marginBottom: 4,
                    fontWeight: 700,
                  }}
                >
                  CASE 0{currentSceneIdx + 1} / 0{p.scenes.length}
                </div>
                <div style={{ fontSize: 40, fontWeight: 900, color: '#fff', lineHeight: 1.05 }}>
                  {currentScene.scenario}
                </div>
                <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.85)', fontStyle: 'italic', marginTop: 4 }}>
                  {currentScene.scenarioEn}
                </div>
              </div>
              <div
                style={{
                  padding: '12px 22px',
                  background: 'rgba(255,255,255,0.18)',
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderRadius: 14,
                  textAlign: 'center',
                  flexShrink: 0,
                }}
              >
                <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.1em' }}>⏱ 救援窗口</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: '#fff', marginTop: 2 }}>{currentScene.timeWindow}</div>
              </div>
            </div>

            {/* Two-column body: signs (left) + steps (right) */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                gap: 0,
                background: '#fff',
                borderLeft: `2px solid ${currentScene.themeColor}33`,
                borderRight: `2px solid ${currentScene.themeColor}33`,
                minHeight: 0,
              }}
            >
              {/* Signs column */}
              <div
                style={{
                  width: 560,
                  padding: '20px 28px',
                  background: '#fafafa',
                  borderRight: `1px dashed ${currentScene.themeColor}40`,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    fontSize: 24,
                    color: '#94a3b8',
                    fontWeight: 800,
                    marginBottom: 14,
                    letterSpacing: '0.18em',
                  }}
                >
                  ① 识别征兆 · SIGNS
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {currentScene.signs.map((sign, i) => {
                    const sIn = spring({
                      frame: localFrame - 8 - i * 6,
                      fps,
                      from: 0,
                      to: 1,
                      config: { damping: 16, stiffness: 120 },
                    });
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 14,
                          opacity: sIn,
                          transform: `translateX(${(1 - sIn) * 16}px)`,
                          fontSize: 24,
                          color: '#334155',
                          lineHeight: 1.4,
                          padding: '10px 14px',
                          background: '#fff',
                          borderRadius: 10,
                          border: `1px solid ${currentScene.themeColor}20`,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: currentScene.themeColor,
                            color: '#fff',
                            fontSize: 24,
                            fontWeight: 900,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        >
                          {i + 1}
                        </div>
                        <div style={{ flex: 1 }}>{sign}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Steps column */}
              <div
                style={{
                  flex: 1,
                  padding: '20px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontSize: 24,
                    color: '#94a3b8',
                    fontWeight: 800,
                    marginBottom: 14,
                    letterSpacing: '0.18em',
                  }}
                >
                  ② 三步急救 · ACTIONS
                </div>
                <div style={{ display: 'flex', gap: 14, flex: 1, minHeight: 0 }}>
                  {currentScene.steps.map((step, i) => {
                    const sIn = spring({
                      frame: localFrame - 24 - i * 10,
                      fps,
                      from: 0,
                      to: 1,
                      config: { damping: 16, stiffness: 120 },
                    });
                    return (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '18px 18px',
                          background: '#fff',
                          borderRadius: 14,
                          border: `2px solid ${currentScene.themeColor}30`,
                          opacity: sIn,
                          transform: `translateY(${(1 - sIn) * 20}px)`,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                          position: 'relative',
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            marginBottom: 12,
                          }}
                        >
                          <div
                            style={{
                              width: 56,
                              height: 56,
                              borderRadius: 14,
                              background: currentScene.themeColor,
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 28,
                              flexShrink: 0,
                              boxShadow: `0 4px 12px ${currentScene.themeColor}55`,
                            }}
                          >
                            {step.icon}
                          </div>
                          <div
                            style={{
                              fontSize: 24,
                              fontWeight: 900,
                              color: currentScene.themeColor,
                              letterSpacing: '0.18em',
                              padding: '4px 10px',
                              border: `1.5px solid ${currentScene.themeColor}`,
                              borderRadius: 6,
                            }}
                          >
                            STEP {i + 1}
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: 32,
                            fontWeight: 900,
                            color: currentScene.themeColor,
                            lineHeight: 1.2,
                            marginBottom: 8,
                          }}
                        >
                          {step.action}
                        </div>
                        <div style={{ fontSize: 24, color: '#475569', lineHeight: 1.55, flex: 1 }}>
                          {step.detail}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Warning bar */}
            <div
              style={{
                padding: '14px 28px',
                background: '#fef2f2',
                borderRadius: '0 0 20px 20px',
                borderLeft: `2px solid ${currentScene.themeColor}33`,
                borderRight: `2px solid ${currentScene.themeColor}33`,
                borderBottom: `2px solid ${currentScene.themeColor}33`,
                borderTop: `2px solid ${currentScene.themeColor}20`,
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                opacity: interpolate(localFrame, [60, 75], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                }),
                flexShrink: 0,
              }}
            >
              <div style={{ fontSize: 32 }}>⚠️</div>
              <div
                style={{
                  fontSize: 22,
                  color: '#7f1d1d',
                  fontWeight: 800,
                  letterSpacing: '0.18em',
                  padding: '4px 12px',
                  background: '#fee2e2',
                  borderRadius: 6,
                  flexShrink: 0,
                }}
              >
                误区警告
              </div>
              <div style={{ fontSize: 22, color: '#991b1b', lineHeight: 1.45, flex: 1, fontWeight: 600 }}>
                {currentScene.warning}
              </div>
            </div>

            {/* Scene navigator dots */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 10,
                marginTop: 14,
                flexShrink: 0,
              }}
            >
              {p.scenes.map((s, i) => (
                <div
                  key={`dot-${i}`}
                  style={{
                    width: i === currentSceneIdx ? 44 : 14,
                    height: 14,
                    borderRadius: 7,
                    background: i === currentSceneIdx ? s.themeColor : '#cbd5e1',
                    transition: 'width 0.3s',
                  }}
                />
              ))}
            </div>
          </div>
        );
      })()}

      {/* ===== First Aid Kit ===== */}
      {frame >= kitStart - 10 && frame < principleStart + 5 && (
        <div
          style={{
            position: 'absolute',
            top: 150,
            left: 60,
            right: 60,
            bottom: 140,
            opacity:
              interpolate(frame, [kitStart - 10, kitStart + 10], [0, 1], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              }) *
              interpolate(frame, [principleStart - 15, principleStart + 5], [1, 0], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              }),
            background: '#fff',
            borderRadius: 20,
            border: '2px solid #dc2626',
            padding: '28px 36px',
            boxShadow: '0 8px 24px rgba(220,38,38,0.15)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22, flexShrink: 0 }}>
            <div style={{ fontSize: 52 }}>🧰</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 38, fontWeight: 900, color: '#dc2626', lineHeight: 1.1 }}>
                家庭必备急救包清单
              </div>
              <div style={{ fontSize: 24, color: '#64748b', marginTop: 4 }}>
                放在易取的位置 · 每半年检查一次 · 共 {p.kit.length} 件
              </div>
            </div>
            <div
              style={{
                fontSize: 22,
                color: '#7f1d1d',
                fontWeight: 700,
                padding: '10px 18px',
                background: '#fee2e2',
                borderRadius: 10,
                border: '2px solid #dc2626',
              }}
            >
              FIRST-AID KIT
            </div>
          </div>
          <div
            style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gridTemplateRows: 'repeat(2, 1fr)',
              gap: 16,
              minHeight: 0,
            }}
          >
            {p.kit.map((k, i) => {
              const sIn = spring({
                frame: frame - (kitStart + i * 5),
                fps,
                from: 0,
                to: 1,
                config: { damping: 16, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 10,
                    padding: '18px 16px',
                    background: '#fafafa',
                    borderRadius: 14,
                    border: '1.5px solid #e2e8f0',
                    opacity: sIn,
                    transform: `translateY(${(1 - sIn) * 20}px) scale(${0.94 + sIn * 0.06})`,
                  }}
                >
                  <div style={{ fontSize: 56, lineHeight: 1 }}>{k.icon}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{k.name}</div>
                  <div style={{ fontSize: 22, color: '#64748b', lineHeight: 1.4 }}>{k.use}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== Golden Principles ===== */}
      {frame >= principleStart - 10 && (
        <div
          style={{
            position: 'absolute',
            top: 150,
            left: 60,
            right: 60,
            bottom: 140,
            display: 'flex',
            gap: 22,
            opacity: interpolate(frame, [principleStart - 10, principleStart + 10], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            }),
          }}
        >
          {/* Left: Golden Principles */}
          <div
            style={{
              flex: 1.6,
              padding: '32px 36px',
              background: 'linear-gradient(135deg, #1e293b, #0f172a)',
              borderRadius: 20,
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 12px 32px rgba(15,23,42,0.3)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, flexShrink: 0 }}>
              <div style={{ fontSize: 44 }}>📜</div>
              <div>
                <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1.1 }}>
                  急救黄金原则
                </div>
                <div style={{ fontSize: 20, color: '#94a3b8', letterSpacing: '0.18em', marginTop: 2 }}>
                  GOLDEN PRINCIPLES
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, justifyContent: 'space-around' }}>
              {p.goldenPrinciples.map((rule, i) => {
                const sIn = spring({
                  frame: frame - (principleStart + i * 10),
                  fps,
                  from: 0,
                  to: 1,
                  config: { damping: 16, stiffness: 120 },
                });
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 18,
                      padding: '14px 18px',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 12,
                      opacity: sIn,
                      transform: `translateX(${(1 - sIn) * 24}px)`,
                    }}
                  >
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        background: '#dc2626',
                        borderRadius: 12,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 900,
                        fontSize: 22,
                        flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(220,38,38,0.4)',
                      }}
                    >
                      {i + 1}
                    </div>
                    <div style={{ fontSize: 24, color: '#e2e8f0', lineHeight: 1.45, flex: 1 }}>
                      {rule}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Hotlines + Heart visual */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div
              style={{
                padding: '24px 26px',
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                borderRadius: 20,
                color: '#fff',
                boxShadow: '0 12px 32px rgba(220,38,38,0.3)',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div
                  style={{
                    fontSize: 52,
                    transform: `scale(${heartRate})`,
                    transformOrigin: 'center',
                  }}
                >
                  💗
                </div>
                <div>
                  <div style={{ fontSize: 20, opacity: 0.85, letterSpacing: '0.18em', fontWeight: 700 }}>
                    EMERGENCY CALL
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 900, lineHeight: 1.1, marginTop: 2 }}>
                    紧急呼叫电话
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
              {p.emergencyHotline.map((h, i) => {
                const sIn = spring({
                  frame: frame - (principleStart + 10 + i * 8),
                  fps,
                  from: 0,
                  to: 1,
                  config: { damping: 16, stiffness: 120 },
                });
                return (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      padding: '20px 24px',
                      background: '#fff',
                      border: '2.5px solid #dc2626',
                      borderRadius: 16,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 20,
                      opacity: sIn,
                      transform: `translateY(${(1 - sIn) * 16}px)`,
                      boxShadow: '0 6px 16px rgba(220,38,38,0.12)',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 56,
                        fontWeight: 900,
                        color: '#dc2626',
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                        minWidth: 130,
                      }}
                    >
                      {h.number}
                    </div>
                    <div style={{ flex: 1, borderLeft: '2px dashed #fecaca', paddingLeft: 20 }}>
                      <div style={{ fontSize: 26, color: '#0f172a', fontWeight: 800 }}>{h.label}</div>
                      <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 4, letterSpacing: '0.15em' }}>
                        24/7 ON-CALL
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ===== Closing call to action ===== */}
      {frame >= closingStart - 10 && (
        <div
          style={{
            position: 'absolute',
            top: 150,
            left: 60,
            right: 60,
            bottom: 140,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: interpolate(frame, [closingStart - 10, closingStart + 15], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            }),
          }}
        >
          <div
            style={{
              padding: '52px 64px',
              background: 'linear-gradient(135deg, #dc2626, #ea580c)',
              borderRadius: 28,
              color: '#fff',
              boxShadow: '0 20px 60px rgba(220,38,38,0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: 36,
              maxWidth: 1500,
            }}
          >
            <div
              style={{
                fontSize: 120,
                transform: `scale(${heartRate})`,
                transformOrigin: 'center',
                flexShrink: 0,
              }}
            >
              💝
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  letterSpacing: '0.25em',
                  opacity: 0.85,
                  marginBottom: 12,
                }}
              >
                SHARE · SAVE · ACT
              </div>
              <div
                style={{
                  fontSize: 38,
                  lineHeight: 1.4,
                  fontWeight: 800,
                  textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                }}
              >
                {p.closingCall}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, totalFrames], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 5,
          background: 'linear-gradient(90deg, #dc2626, #ea580c, #f97316, #b91c1c, #7c3aed)',
        }}
      />
    </AbsoluteFill>
  );
};
